import { FE_SESSION_ID_KEY } from 'constants/session';
import { isForcedDisconnectReason, useAuthStore } from 'lib/stores/authStore';
import { useRegistrationSessionStore } from 'lib/stores/registrationSessionStore';
import logProviderFactory from 'utils/logger/logProviderFactory';
import { removeThemeCookie } from 'utils/theme-cookie';

/**
 * Auth-driven session teardown. Subscribes to authStore transitions set by the
 * transport layer and fires the cleanup that follows from them:
 *
 * - A forced disconnect (session_expired / maintenance / unavailability) clears
 *   the per-session storage artifacts here and hands off to `onForcedDisconnect`
 *   for the rest of teardown (appEffects registers that callback — cancelling
 *   the debounced notification-pref save and disposing the feature stores).
 *   'manual' logouts intentionally don't fire it: explicit sign-out runs its
 *   own teardown via UserStore.endSession(), so firing here would double up.
 * - A 401 response (unauthorizedResponseToken bump) clears the registration-local
 *   user id, whether or not it comes with a full logout.
 *
 * Kept separate from sessionEffects.ts (the analytics fanout) because that one
 * only runs where SessionEffectsBootstrap is mounted, while this must cover
 * every AppEffectsBootstrap usage — including the embedded (charts) layout, which
 * deliberately loads no analytics.
 */
export function startSessionTeardownEffects(onForcedDisconnect: () => void): () => void {
    return useAuthStore.subscribe((state, prev) => {
        if (
            state.logoutReason !== prev.logoutReason &&
            state.logoutReason !== null &&
            isForcedDisconnectReason(state.logoutReason)
        ) {
            localStorage.removeItem(FE_SESSION_ID_KEY);
            localStorage.removeItem('kalshi-color-palette');
            removeThemeCookie();
            onForcedDisconnect();
        }

        if (state.unauthorizedResponseToken !== prev.unauthorizedResponseToken) {
            useRegistrationSessionStore.getState().actions.userIdCleared();
            logProviderFactory.cleanUser();
        }
    });
}
